import { BaselineSection } from '../BaselineSection';
import { type StepForm, WizardField } from '../WizardField';

interface Props {
  form: StepForm;
}

const BASELINE_FIELDS = [
  {
    name: 'step4.baselineAnnualTaxableSales',
    label: 'Existing annual taxable sales',
    format: 'currency' as const,
  },
  {
    name: 'step4.baselineAnnualRoomRevenue',
    label: 'Existing annual room revenue',
    format: 'currency' as const,
  },
  {
    name: 'step4.baselineAnnualMixedBeverageReceipts',
    label: 'Existing annual mixed-beverage receipts',
    format: 'currency' as const,
  },
];

export function Step4Revenue({ form }: Props) {
  const projectType = form.watch('step1.projectType');
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-800">Revenue</h3>
        <p className="mt-0.5 text-sm text-slate-500">
          Gross taxable sales and industry-specific revenues at stabilization.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <WizardField
            form={form}
            name="step4.annualTaxableSales"
            label="Annual taxable sales at stabilization"
            format="currency"
            hint="Gross taxable sales subject to local sales tax"
          />
        </div>

        <div className="col-span-2">
          <p className="mb-2 text-sm font-medium text-slate-700">Revenue ramp-up</p>
          <div className="grid grid-cols-2 gap-4">
            <WizardField
              form={form}
              name="step4.revenueRampYear1"
              label="Year 1"
              format="percent"
              min={0}
              max={1}
              step={0.05}
              hint="Revenue as % of full stabilization"
            />
            <WizardField
              form={form}
              name="step4.revenueRampYear2"
              label="Year 2"
              format="percent"
              min={0}
              max={1}
              step={0.05}
              hint="Year 3+ assumed at 100%"
            />
          </div>
        </div>

        <div className="col-span-2">
          <p className="mb-2 text-sm font-medium text-slate-700">
            Industry-specific revenue{' '}
            <span className="font-normal text-slate-400">(leave at $0 if not applicable)</span>
          </p>
          <div className="grid grid-cols-2 gap-4">
            <WizardField
              form={form}
              name="step4.annualRoomRevenue"
              label="Annual gross room revenue"
              format="currency"
              hint="Hotels and lodging only"
            />
            <WizardField
              form={form}
              name="step4.annualMixedBeverageReceipts"
              label="Annual mixed-beverage receipts"
              format="currency"
              hint="Bars, hotels with F&B service"
            />
          </div>
        </div>
      </div>

      <BaselineSection form={form} projectType={projectType} fields={BASELINE_FIELDS} />
    </div>
  );
}
